import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { processLock } from '@supabase/auth-js';

/**
 * Singleton verdadero via `globalThis`. Vite HMR + React StrictMode pueden
 * re-evaluar el módulo y crear MÚLTIPLES instancias del client; cada una
 * compite por el Web Lock de @supabase/auth-js (`Lock was stolen by another
 * request`). El singleton global persiste entre re-evaluaciones de módulo.
 */
const SINGLETON_KEY = '__aosenuma_supabase_client__';
type GlobalWithClient = typeof globalThis & { [SINGLETON_KEY]?: SupabaseClient };

export function getSupabase(): SupabaseClient {
  const g = globalThis as GlobalWithClient;
  if (g[SINGLETON_KEY]) return g[SINGLETON_KEY]!;
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const anon = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  if (!url || !anon) {
    throw new Error(
      '[lib-supabase] Faltan VITE_PUBLIC_SUPABASE_URL / VITE_PUBLIC_SUPABASE_ANON_KEY',
    );
  }
  g[SINGLETON_KEY] = createClient(url, anon, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Implicit por compatibilidad multi-origin (3 SPAs en puertos distintos
      // en dev). PKCE rompe el cross-origin porque el code_verifier vive en
      // localStorage del origin que solicitó, y el callback puede recibirse
      // en otro. En prod con dominios *.aosenumareport.com + cookie storage
      // compartido se puede subir a PKCE en Fase 5 (hardening).
      flowType: 'implicit',
      /**
       * processLock (memoria, single-tab) en lugar del default navigatorLock
       * (Web Locks API global). Bug Diego 2026-05-11:
       *
       *   @supabase/gotrue-js: Lock "lock:sb-…-auth-token" was not released
       *     within 5000ms ... Forcefully acquiring the lock (x6)
       *   Unhandled Promise Rejection: AbortError: Lock was stolen by another
       *     request
       *   Fetch API cannot load https://…/v_parcelas_tecnicas due to access
       *     control checks  ← Safari traduce el fetch abortado como CORS
       *
       * Causa raíz: bajo CapturaPage corren ~6 hooks Supabase paralelos al
       * mount + React Strict Mode dobla los mounts. El navigatorLock
       * serializa cross-tab Y intra-tab; bajo esta carga sobrepasa los 5s
       * de timeout interno → robo de lock → cascada de fetches abortados.
       *
       * processLock serializa solo intra-tab (Promise queue en memoria).
       * Tradeoff aceptado: si el user abre 2 pestañas del mismo SaaS, ambas
       * pueden refrescar el JWT simultáneamente. Esto es idempotente —
       * Supabase devuelve tokens válidos a ambas. No es problema funcional;
       * a lo mucho un refresh extra por minuto.
       */
      lock: processLock,
    },
  });
  return g[SINGLETON_KEY]!;
}

/** Permite omitir queries cuando faltan vars (p. ej. panel técnico en local sin Supabase). */
export function isSupabaseConfigured(): boolean {
  const url = typeof import.meta.env.VITE_PUBLIC_SUPABASE_URL === 'string'
    ? import.meta.env.VITE_PUBLIC_SUPABASE_URL.trim()
    : '';
  const anon = typeof import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY === 'string'
    ? import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY.trim()
    : '';
  return url.length > 0 && anon.length > 0;
}

/** Acceso lazy al cliente unico. */
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    return Reflect.get(getSupabase(), prop);
  },
});
